/**
 * @feature quote-requests
 * @layer presentation/components
 *
 * DeliveryInfoCard - 3-Column Card (배송 정보)
 * Displays delivery deadline and address information
 * Based on QuoteDetailPage design
 */

import { type FC } from "react";
import { Truck, MapPin, Globe, ShieldAlert } from "@semo/icons";
import { cn } from "../../../utils/cn";
import type { DeliveryInfoCardProps } from "../types";
import { formatDueDate, formatAddressDetails } from "../utils/formatters";
import { getEligibleRegions } from "../utils/regionAdjacency";

/**
 * 3-Column Card: Delivery Information
 * Shows:
 * - 납기 희망일 (Due date with flexibility)
 * - 배송지 (Delivery address)
 */
export const DeliveryInfoCard: FC<DeliveryInfoCardProps> = ({
  data,
  onEdit,
  selectedAddress,
  mode,
}) => {
  // Extract delivery data
  const {
    dueDate,
    dueDateType,
    dueDateValue,
    dueDateDays,
    sameRegionOnly,
    address: dataAddress,
  } = data;

  // Format due date with all parameters
  const formattedDueDate = formatDueDate(
    dueDate,
    dueDateType,
    dueDateValue,
    dueDateDays,
  );

  // Format address
  const addressToDisplay = selectedAddress ?? dataAddress;

  // 도로명(주소록 streetAddress · 응답 addressLine1 → roadAddress)과 상세 주소까지
  // 보여 준다 — 시·구·동만 보여 고른 배송지를 확인할 수 없던 것(GS A2-24).
  const addressDisplay = addressToDisplay
    ? formatAddressDetails({
        province: addressToDisplay.province,
        city: addressToDisplay.city,
        district: addressToDisplay.district,
        roadAddress:
          addressToDisplay.roadAddress ??
          addressToDisplay.address ??
          addressToDisplay.streetAddress,
        addressDetail: addressToDisplay.addressDetail,
      })
    : "배송지 미등록";

  // Address alias/label — 주소록·응답의 `label`(예: «회사») 도 읽는다(GS A2-24).
  const getAddressLabel = (addr: unknown) => {
    if (addr && typeof addr === "object") {
      if ("addressAlias" in addr && typeof addr.addressAlias === "string")
        return addr.addressAlias;
      if ("alias" in addr && typeof addr.alias === "string") return addr.alias;
      if (
        "label" in addr &&
        typeof addr.label === "string" &&
        addr.label.trim() !== ""
      )
        return addr.label;
    }
    return "등록된 배송지";
  };
  const addressLabel = getAddressLabel(addressToDisplay);

  return (
    <div className={cn("bg-white", "rounded-[20px] p-5 sm:p-6")}>
      {/* Card Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          {/* Icon */}
          <div className="w-9 h-9 rounded-[10px] bg-primary/5 flex items-center justify-center text-primary">
            <Truck className="w-4.5 h-4.5" />
          </div>
          {/* Title */}
          <h3 className="text-lg font-semibold text-gray-900 tracking-tight">
            배송 정보
          </h3>
        </div>

        {/* Edit button - optional */}
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className={cn(
              "px-2 py-1",
              "text-xs font-semibold",
              "text-gray-400",
              "hover:text-primary transition-colors",
            )}
          >
            수정
          </button>
        )}
      </div>

      {/* Card Content */}
      <div className="space-y-4">
        {/* Due Date */}
        <div>
          <span className="text-2xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">
            희망 납기일
          </span>
          <span className="text-base font-semibold text-gray-900 tracking-tight">
            {formattedDueDate}
          </span>
        </div>

        {/* Address */}
        <div>
          <span className="text-2xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">
            배송지
          </span>
          {addressToDisplay ? (
            <div className="space-y-1">
              <div className="text-base font-semibold text-gray-900 tracking-tight">
                {addressLabel}
              </div>
              <div className="text-xs text-gray-500 leading-snug font-medium">
                {addressDisplay}
              </div>
            </div>
          ) : (
            <span className="text-base font-semibold text-gray-900">
              배송지 미등록
            </span>
          )}
        </div>

        {/* Same Region Only Setting */}
        <div className="flex items-center gap-2 text-xs pt-3 border-t border-gray-50 font-semibold">
          {sameRegionOnly ? (
            <>
              <MapPin className="w-4 h-4 text-primary" />
              <span className="text-primary">같은 지역 업체 우선</span>
            </>
          ) : (
            <>
              <Globe className="w-4 h-4 text-gray-300" />
              <span className="text-gray-400">전국 배송</span>
            </>
          )}
        </div>

        {/*
         * 관리자 전용 — 참여 가능 지역 상세.
         * "동일 지역만 허용" 이 켜진 발주에서 어느 시/도 공급사에게 연락해야 하는지 즉시 파악할 수
         * 있도록, 발주자 시/도 + 인접 2개 시/도 (총 3개) 를 명시적으로 나열한다. buyer/supplier
         * 뷰에는 노출하지 않는다 (기존 "같은 지역 업체 우선" 문구만으로 충분).
         */}
        {sameRegionOnly && mode === "admin" && addressToDisplay?.province && (
          <RegionEligibilityDetail province={addressToDisplay.province} />
        )}
      </div>
    </div>
  );
};

/**
 * 참여 가능 지역 리스트 (관리자 전용 서브 컴포넌트).
 * `getEligibleRegions` 가 반환한 시/도들을 pill 형태로 보여준다.
 */
const RegionEligibilityDetail: FC<{ province: string }> = ({ province }) => {
  const { displayNames, unknown } = getEligibleRegions(province);
  if (displayNames.length === 0) return null;

  return (
    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
      <div className="flex items-center gap-1.5">
        <ShieldAlert className="w-3.5 h-3.5 text-amber-600 shrink-0" />
        <span className="text-2xs font-semibold text-amber-900">
          참여 가능 지역 ({displayNames.length}곳)
        </span>
      </div>
      <p className="mt-1 text-2xs leading-relaxed text-amber-800">
        발주자 시/도 및 인접한 2개 시/도 공급사만 참여할 수 있습니다.
      </p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {displayNames.map((name) => (
          <span
            key={name}
            className="inline-flex items-center h-6 px-2 rounded-full bg-white text-amber-900 border border-amber-200 text-2xs font-semibold"
          >
            {name}
          </span>
        ))}
      </div>
      {unknown && (
        <p className="mt-1.5 text-2xs text-amber-700">
          ⚠ 인접 지역 매핑을 찾지 못해 발주자 시/도만 노출합니다.
        </p>
      )}
    </div>
  );
};
